import { Download, FileText } from 'lucide-react'
import { useState } from 'react'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { messages } from '@/lib/messages'
import { ApiError } from '@/lib/problem'
import { fetchRunPdf } from '@/services/bi/runs'

const t = messages.documents.runs.pdf

/**
 * "PDF önizle": kopya sayfası, kopya listesi ve baskı sayfası aynı düğmeyi kullanır.
 * Sunucu PDF'ini getirip tarayıcının PDF görüntüleyicisiyle dialogda gösterir; "İndir" aynı dosyayı kaydeder.
 */
export function RunPdfButton({
  reportId,
  runId,
  disabled,
  variant = 'outline',
  size,
}: {
  reportId: string
  runId: string
  disabled?: boolean
  variant?: 'outline' | 'default'
  size?: 'sm'
}) {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string>()
  const [pdf, setPdf] = useState<{ url: string; filename: string }>()

  const onClick = async () => {
    setBusy(true)
    setError(undefined)
    try {
      const { blob, filename } = await fetchRunPdf(reportId, runId)
      setPdf({ url: URL.createObjectURL(blob), filename })
    } catch (failure) {
      setError((failure instanceof ApiError && t.errors[failure.code]) || t.failed)
    } finally {
      setBusy(false)
    }
  }

  const close = () => {
    if (pdf) URL.revokeObjectURL(pdf.url)
    setPdf(undefined)
  }

  const download = () => {
    if (!pdf) return
    const link = document.createElement('a')
    link.href = pdf.url
    link.download = pdf.filename
    link.click()
  }

  return (
    <div className="flex flex-col items-end gap-1">
      <Button
        variant={variant}
        size={size}
        disabled={disabled || busy}
        onClick={() => void onClick()}
      >
        <FileText />
        {busy ? t.busy : t.action}
      </Button>
      {error && (
        <p role="alert" className="text-destructive text-xs">
          {error}
        </p>
      )}
      <Dialog open={!!pdf} onOpenChange={(open) => !open && close()}>
        <DialogContent className="flex h-[90vh] flex-col sm:max-w-5xl">
          <DialogHeader>
            <DialogTitle>{pdf?.filename}</DialogTitle>
          </DialogHeader>
          <DialogBody className="flex min-h-0 flex-1 flex-col">
            {pdf && (
              <iframe
                title={t.frameTitle}
                src={pdf.url}
                className="border-border min-h-0 flex-1 border"
              />
            )}
          </DialogBody>
          <DialogFooter>
            <Button onClick={download}>
              <Download />
              {t.download}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
